import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import i18n from '@risksmart-app/i18n/src/i18n';
import { useMemo } from 'react';
import { useGetControlsRegister } from 'src/hooks/queries/control/useGetControlsRegister';
import {
  useGetIndicatorRegister,
  type UseGetIndicatorRegisterArgs,
} from 'src/hooks/queries/indicator/useGetIndicatorRegister';
import { useLabelledFields as useControlLabelledFields } from 'src/pages/controls/useLabelledFields';
import { useLabelledFields as useIndicatorLabelledFields } from 'src/pages/indicators/useLabelledFields';

import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import {
  type IndicatorRowSlice,
  indicatorSideCategoryGetters,
  indicatorSideFieldConfig,
  prefixedLabel,
  resolveIndicatorSideRow,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

type GetIndicatorsData = NonNullable<
  ReturnType<typeof useGetIndicatorRegister>['data']
>;

export type IndicatorControlRow = TableRecord &
  IndicatorRowSlice & {
    Id: string;
    ControlId: string;
    ControlFriendlyId: string;
    ControlTitle: string;
    ControlType: string;
    ControlDesignEffectiveness: string;
    ControlPerformanceEffectiveness: string;
    ControlOverallEffectiveness: string;
    ControlTestScheduleStatus: string;
  };

export default createDataSource<
  IndicatorControlRow,
  UseGetIndicatorRegisterArgs,
  GetIndicatorsData
>({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Control, Parent_Type_Enum.Indicator],
  useWidgetQuery: useGetIndicatorRegister,
  useTablePropsHook: (
    data: GetIndicatorsData | undefined,
    options: StatefulTableOptions<IndicatorControlRow>
  ) => {
    const fields = useMemo<TableFields<IndicatorControlRow>>(
      () => ({
        ControlFriendlyId: simpleField(
          prefixedLabel('control_one', i18n.t('columns.id'))
        ),
        ControlTitle: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.title'))
        ),
        ControlType: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.type'))
        ),
        ControlDesignEffectiveness: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.design_effectiveness')
          )
        ),
        ControlPerformanceEffectiveness: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.performance_effectiveness')
          )
        ),
        ControlOverallEffectiveness: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.effectiveness'))
        ),
        ControlTestScheduleStatus: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.testScheduleStatus')
          )
        ),
        ...indicatorSideFieldConfig<IndicatorControlRow>(),
      }),
      []
    );

    const indicatorsLabelled = useIndicatorLabelledFields(data?.indicator);
    const { data: controlData, loading: controlsLoading } =
      useGetControlsRegister({ queryArgs: {} });
    const controlsLabelled = useControlLabelledFields(controlData?.control);

    const joinedData = useMemo<IndicatorControlRow[] | undefined>(() => {
      if (!indicatorsLabelled || !controlsLabelled) {
        return undefined;
      }

      const controlMap = new Map(controlsLabelled.map((c) => [c.Id, c]));

      return indicatorsLabelled.flatMap((indicator) =>
        (indicator.parents ?? [])
          .filter(
            (p) =>
              p.parent?.ObjectType === Parent_Type_Enum.Control && p.parent.Id
          )
          .map((parent) => {
            const control = controlMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${indicator.Id}`,
              ControlId: parent.parent!.Id,
              ControlFriendlyId: control?.SequentialIdLabel ?? '',
              // Partner title falls back to '' (see note in indicatorRisk.ts).
              ControlTitle: control?.Title ?? '',
              ControlType: control?.Type ?? '',
              ControlDesignEffectiveness:
                control?.DesignEffectivenessLabelled ?? '',
              ControlPerformanceEffectiveness:
                control?.PerformanceEffectivenessLabelled ?? '',
              ControlOverallEffectiveness:
                control?.OverallEffectivenessLabelled ?? '',
              ControlTestScheduleStatus:
                control?.TestScheduleStatusLabelled ?? '',
              ...resolveIndicatorSideRow(indicator),
            };
          })
      );
    }, [indicatorsLabelled, controlsLabelled]);

    const tableProps = useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('control_indicator_other'),
      options
    );

    // The join stays undefined until the secondary controls query lands —
    // report that window as loading rather than "no data" (RSP-6539).
    return { ...tableProps, isEnrichmentLoading: controlsLoading };
  },
  entityNamePlural: 'control_indicator_other',
  entityNameSingular: 'control_indicator_one',
  fields: 'controlIndicators.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'controlFriendlyId',
      entityGroup: 'control_one',
      name: () => prefixedLabel('control_one', i18n.t('columns.id')),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlFriendlyId'),
    },
    {
      id: 'controlTitle',
      fieldId: 'Title',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel('control_one', i18n.t('controls.columns.title')),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlTitle'),
    },
    {
      id: 'controlType',
      fieldId: 'Type',
      entityGroup: 'control_one',
      name: () => prefixedLabel('control_one', i18n.t('controls.columns.type')),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlType'),
    },
    {
      id: 'controlDesignEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.design_effectiveness')
        ),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlDesignEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlDesignEffectiveness'
      ),
      ratingColourKey: 'design_effectiveness',
    },
    {
      id: 'controlPerformanceEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.performance_effectiveness')
        ),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlPerformanceEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlPerformanceEffectiveness'
      ),
      ratingColourKey: 'performance_effectiveness',
    },
    {
      id: 'controlOverallEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel('control_one', i18n.t('controls.columns.effectiveness')),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlOverallEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlOverallEffectiveness'
      ),
      ratingColourKey: 'effectiveness',
    },
    {
      id: 'controlTestScheduleStatus',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.testScheduleStatus')
        ),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlTestScheduleStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlTestScheduleStatus'
      ),
    },
    ...indicatorSideCategoryGetters<IndicatorControlRow>(),
  ],
});
